import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { FlaskConical, Plus, RefreshCw, Settings, Trash2, Brain, Search, LogOut, Upload, FileText, Sparkles, Loader2, Globe } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { ThemeToggle } from '@/components/ThemeToggle';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    study_type: 'all',
    evidence_level: 'all',
    limit: 50
  });
  const [stats, setStats] = useState({ total_articles: 0, total_scores: 0, total_topics: 0 });
  const [weights, setWeights] = useState({
    views_weight: 0.25,
    downloads_weight: 0.30,
    twitter_weight: 0.15,
    citation_weight: 0.30
  });

  // Edit state
  const [editingArticle, setEditingArticle] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Ingest form
  const [ingestForm, setIngestForm] = useState({
    doi: '',
    pdf_text: '',
    title: '',
    abstract: '',
    authors: '',
    journal: '',
    publication_year: ''
  });

  // PDF upload state
  const [uploadingPdfFor, setUploadingPdfFor] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [selectedArticleForPdf, setSelectedArticleForPdf] = useState(null);
  const [generatingSeo, setGeneratingSeo] = useState(false);
  const [submittingSitemap, setSubmittingSitemap] = useState(false);
  const [seoDashboard, setSeoDashboard] = useState(null);
  const [seoLoading, setSeoLoading] = useState(false);
  const [bulkSeoRunning, setBulkSeoRunning] = useState(false);
  const [seoSubmissions, setSeoSubmissions] = useState([]);

  // Check authentication on mount
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('admin_authenticated');
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    loadArticles();
    loadStats();
    loadWeights();
    loadSeoDashboard();
    loadSeoSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const data = await api.getAllArticles(filters);
      setArticles(data);
      setFilteredArticles(data); // Initialize filtered articles
    } catch (error) {
      toast.error('Failed to load articles');
    }
    setLoading(false);
  };

  const loadSeoDashboard = async () => {
    setSeoLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/admin/seo-dashboard`);
      if (resp.ok) setSeoDashboard(await resp.json());
    } catch { /* silent */ }
    setSeoLoading(false);
  };

  const loadSeoSubmissions = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/admin/seo-submissions`);
      if (resp.ok) {
        const data = await resp.json();
        setSeoSubmissions(data.submissions || []);
      }
    } catch { /* silent */ }
  };

  const handleBulkGenerateSeo = async () => {
    setBulkSeoRunning(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/admin/bulk-generate-seo`, { method: 'POST' });
      const data = await resp.json();
      if (data.success) {
        toast.success(data.message);
        setTimeout(() => { loadSeoDashboard(); loadSeoSubmissions(); }, 15000);
      } else {
        toast.error('Bulk SEO generation failed');
      }
    } catch (err) {
      toast.error('Failed: ' + err.message);
    } finally {
      setBulkSeoRunning(false);
    }
  };

  // Search function
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredArticles(articles);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = articles.filter(article => {
      // Search in title
      if (article.title?.toLowerCase().includes(query)) return true;
      
      // Search in DOI
      if (article.doi?.toLowerCase().includes(query)) return true;
      
      // Search in authors
      if (article.authors && Array.isArray(article.authors)) {
        const authorsString = article.authors.join(' ').toLowerCase();
        if (authorsString.includes(query)) return true;
      }
      
      // Search in journal
      if (article.journal?.toLowerCase().includes(query)) return true;
      
      return false;
    });
    
    setFilteredArticles(filtered);
  }, [searchQuery, articles]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_authenticated');
    sessionStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const handlePdfUpload = async (articleId) => {
    if (!pdfFile) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploadingPdfFor(articleId);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);

      const response = await fetch(`${API_URL}/api/admin/articles/${articleId}/upload-pdf`, {
        method: 'POST',
        body: formData
      });

      // Parse response ONCE with proper error handling
      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        void 0;
        throw new Error('Server returned invalid response. Please check backend logs.');
      }

      if (!response.ok) {
        throw new Error(result.detail || result.message || 'Failed to upload PDF');
      }

      toast.success(`PDF uploaded! AI analysis started.`);
      setIsPdfDialogOpen(false);
      setPdfFile(null);
      setSelectedArticleForPdf(null);
      loadArticles();
    } catch (error) {
      void 0;
      toast.error(error.message || 'Failed to upload PDF');
    } finally {
      setUploadingPdfFor(null);
    }
  };

  const openPdfDialog = (article) => {
    setSelectedArticleForPdf(article);
    setPdfFile(null);
    setIsPdfDialogOpen(true);
  };

  const loadStats = async () => {
    try {
      const data = await api.getStats();
      setStats(data);
    } catch (error) {
      void 0;
    }
  };

  const loadWeights = async () => {
    try {
      const data = await api.getVisibilityWeights();
      setWeights(data);
    } catch (error) {
      void 0;
    }
  };

  const handleSubmitSitemap = async () => {
    setSubmittingSitemap(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/admin/submit-sitemap`, { method: 'POST' });
      const data = await resp.json();
      if (data.success) {
        toast.success(`Submitted ${data.total_urls} URLs to search engines`);
        if (data.note) toast.info(data.note, { duration: 8000 });
      } else {
        toast.error('Submission failed');
      }
    } catch (err) {
      toast.error('Failed: ' + err.message);
    } finally {
      setSubmittingSitemap(false);
    }
  };

  const handleIngest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        doi: ingestForm.doi || undefined,
        title: ingestForm.title || undefined,
        abstract: ingestForm.abstract || undefined,
        pdf_text: ingestForm.pdf_text || undefined,
        journal: ingestForm.journal || undefined,
        publication_year: ingestForm.publication_year ? parseInt(ingestForm.publication_year) : undefined,
        authors: ingestForm.authors ? ingestForm.authors.split(',').map(a => a.trim()).filter(Boolean) : undefined,
      };
      const result = await api.ingestArticle(payload);
      toast.success(`Article ingested successfully! Impact Index: ${result.impact_index.toFixed(1)}`);
      setIngestForm({ doi: '', pdf_text: '', title: '', abstract: '', authors: '', journal: '', publication_year: '' });
      loadArticles();
      loadStats();
    } catch (error) {
      toast.error('Failed to ingest article: ' + error.message);
    }
    setLoading(false);
  };

  const handleRecalculate = async (doi) => {
    try {
      const result = await api.recalculateImpact(doi);
      toast.success(`Score recalculated! New Impact Index: ${result.impact_index.toFixed(1)}`);
      loadArticles();
    } catch (error) {
      toast.error('Failed to recalculate: ' + error.message);
    }
  };

  const handleUpdateWeights = async () => {
    try {
      await api.updateVisibilityWeights(weights);
      toast.success('Visibility weights updated successfully');
    } catch (error) {
      toast.error('Failed to update weights: ' + error.message);
    }
  };

  const handleDeleteArticle = async (articleId, articleTitle) => {
    try {
      await api.deleteArticle(articleId);
      toast.success(`Article "${articleTitle}" deleted successfully`);
      loadArticles();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete article: ' + error.message);
    }
  };

  const handleAnalyzeArticle = async (articleId, articleTitle) => {
    setAnalyzingId(articleId);
    try {
      toast.info('Analysis started. This may take 30-60 seconds...', { duration: 5000 });
      
      // Trigger analysis
      const result = await api.analyzeArticle(articleId);
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 40; // 40 * 3 sec = 2 minutes max
      
      const checkStatus = async () => {
        attempts++;
        
        if (attempts > maxAttempts) {
          toast.warning('Analysis still processing in background...');
          setAnalyzingId(null);
          await loadArticles();
          return;
        }

        try {
          const articles = await api.getArticles();
          const article = articles.find(a => a.id === articleId);
          
          if (article && article.article_status === 'analysis_complete') {
            toast.success('Analysis complete!');
            setAnalyzingId(null);
            await loadArticles();
            // Navigate to insight page
            navigate(`/research-insight/${articleId}`);
            return;
          } else if (article && article.article_status === 'analysis_failed') {
            toast.error('Analysis failed');
            setAnalyzingId(null);
            await loadArticles();
            return;
          } else {
            // Still processing
            setTimeout(checkStatus, 3000);
          }
        } catch (error) {
          void 0;
          setTimeout(checkStatus, 3000);
        }
      };

      // Start polling after initial delay
      setTimeout(checkStatus, 5000);
      
    } catch (error) {
      toast.error('Failed to analyze article: ' + error.message);
      setAnalyzingId(null);
    }
  };


  const handleReanalyzeArticle = async (articleId) => {
    setAnalyzingId(articleId);
    try {
      toast.info('Re-analyzing with full text. This may take 30-60 seconds...', { duration: 5000 });
      await api.reanalyzeArticle(articleId);
      
      let attempts = 0;
      const checkStatus = async () => {
        attempts++;
        if (attempts > 40) {
          toast.warning('Re-analysis still processing...');
          setAnalyzingId(null);
          await loadArticles();
          return;
        }
        try {
          const articles = await api.getArticles();
          const article = articles.find(a => a.id === articleId);
          if (article && article.article_status === 'analysis_complete') {
            toast.success('Re-analysis complete! Scores updated with full text data.');
            setAnalyzingId(null);
            await loadArticles();
            return;
          } else if (article && article.article_status === 'analysis_failed') {
            toast.error('Re-analysis failed');
            setAnalyzingId(null);
            await loadArticles();
            return;
          }
          setTimeout(checkStatus, 3000);
        } catch { setTimeout(checkStatus, 3000); }
      };
      setTimeout(checkStatus, 5000);
    } catch (error) {
      toast.error('Failed to re-analyze: ' + error.message);
      setAnalyzingId(null);
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setEditForm({
      title: article.title || '',
      abstract: article.abstract || '',
      faq: Array.isArray(article.faq) ? article.faq.map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n') : (article.faq || ''),
      authors: article.authors ? article.authors.join(', ') : '',
      journal: article.journal || '',
      publication_year: article.publication_year || '',
      doi: article.doi || '',
      raw_study_type: article.raw_study_type || '',
      sample_size: article.sample_size || '',
      follow_up_duration_months: article.follow_up_duration_months || '',
      // SEO fields (loaded separately)
      seo_title: '',
      meta_description: '',
      seo_keywords: '',
      slug: '',
      twitter_text: '',
      linkedin_summary: '',
    });
    // Load SEO data (try article.seo_metadata first, then insight)
    const articleSeo = article.seo_metadata || {};
    setEditForm(prev => ({
      ...prev,
      seo_title: articleSeo.seo_title || '',
      meta_description: articleSeo.meta_description || '',
      seo_keywords: (articleSeo.secondary_keywords || []).join(', '),
      slug: articleSeo.slug || '',
    }));
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/admin/articles/${article.id}/seo`)
      .then(r => r.json())
      .then(data => {
        const seo = data.seo_metadata || {};
        const social = data.social_media_content || {};
        setEditForm(prev => ({
          ...prev,
          seo_title: prev.seo_title || seo.seo_title || '',
          meta_description: prev.meta_description || seo.meta_description || '',
          seo_keywords: prev.seo_keywords || (seo.secondary_keywords || []).join(', '),
          slug: prev.slug || seo.slug || '',
          twitter_text: (social.twitter_posts || [])[0] || '',
          linkedin_summary: social.linkedin_summary || '',
        }));
      })
      .catch(() => {});
    setIsEditDialogOpen(true);
  };

  const handleGenerateAcademicSeo = async () => {
    if (!editingArticle) return;
    setGeneratingSeo(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/articles/${editingArticle.id}/generate-academic-seo`, {
        method: 'POST',
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to generate SEO');
      }
      const data = await response.json();
      setEditForm(prev => ({
        ...prev,
        seo_title: data.seo_title || prev.seo_title,
        meta_description: data.meta_description || prev.meta_description,
        seo_keywords: (data.mesh_keywords || []).join(', ') || prev.seo_keywords,
        slug: data.slug || prev.slug,
        twitter_text: data.twitter_post || prev.twitter_text,
        linkedin_summary: data.linkedin_summary || prev.linkedin_summary,
      }));
      toast.success('Academic SEO generated! Review and save.');
    } catch (error) {
      toast.error('SEO generation failed: ' + error.message);
    } finally {
      setGeneratingSeo(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      // Save article metadata
      const seoMeta = {
        seo_title: editForm.seo_title || '',
        meta_description: editForm.meta_description || '',
        secondary_keywords: editForm.seo_keywords ? editForm.seo_keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        slug: editForm.slug || '',
      };
      const updateData = {
        title: editForm.title,
        abstract: editForm.abstract,
        faq: editForm.faq ? editForm.faq.split('\n\n').filter(Boolean).map(pair => {
          const lines = pair.split('\n');
          const q = (lines.find(l => l.startsWith('Q:')) || '').replace('Q:', '').trim();
          const a = (lines.find(l => l.startsWith('A:')) || '').replace('A:', '').trim();
          return q && a ? { question: q, answer: a } : null;
        }).filter(Boolean) : [],
        authors: editForm.authors.split(',').map(a => a.trim()).filter(a => a),
        journal: editForm.journal,
        publication_year: editForm.publication_year === '' ? null : editForm.publication_year,
        doi: editForm.doi,
        raw_study_type: editForm.raw_study_type,
        sample_size: editForm.sample_size === '' ? null : editForm.sample_size,
        follow_up_duration_months: editForm.follow_up_duration_months === '' ? null : editForm.follow_up_duration_months,
        seo_metadata: seoMeta,
      };
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/articles/${editingArticle.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) throw new Error('Failed to update article');

      // Save SEO data
      const seoPayload = {
        seo_metadata: {
          seo_title: editForm.seo_title,
          meta_description: editForm.meta_description,
          secondary_keywords: editForm.seo_keywords ? editForm.seo_keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
          slug: editForm.slug,
        },
        social_media_content: {
          twitter_posts: editForm.twitter_text ? [editForm.twitter_text] : [],
          linkedin_summary: editForm.linkedin_summary,
        },
      };
      await fetch(`${API_URL}/api/admin/articles/${editingArticle.id}/seo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(seoPayload)
      });

      toast.success('Article & SEO updated successfully');
      setIsEditDialogOpen(false);
      loadArticles();
    } catch (error) {
      toast.error('Failed to update article: ' + error.message);
    }
  };


  const getEvidenceLevelColor = (level) => {
    switch (level) {
      case 'Level I': return 'bg-green-100 text-green-800';
      case 'Level II': return 'bg-blue-100 text-blue-800';
      case 'Level III': return 'bg-yellow-100 text-yellow-800';
      case 'Level IV': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <FlaskConical className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
              <span className="text-lg sm:text-xl font-bold text-slate-900">Admin</span>
            </Link>
            <div className="flex items-center gap-1 sm:gap-4">
              <span className="hidden lg:inline text-sm text-slate-600">
                {sessionStorage.getItem('admin_email')}
              </span>
              <Link to="/admin/authors">
                <Button variant="ghost" size="sm" data-testid="nav-authors-btn">Authors</Button>
              </Link>
              <Link to="/explorer" className="hidden sm:inline">
                <Button variant="ghost" size="sm" data-testid="nav-explorer-btn">Explorer</Button>
              </Link>
              <Link to="/" className="hidden sm:inline">
                <Button variant="ghost" size="sm" data-testid="nav-home-btn">Home</Button>
              </Link>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by title, DOI, authors, or journal..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
                data-testid="admin-search-input"
              />
              {searchQuery && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSearchQuery('')}
                >
                  Clear
                </Button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm text-slate-600 mt-2">
                Found {filteredArticles.length} article(s) matching "{searchQuery}"
              </p>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-6 sm:mb-8">
          <Card data-testid="stat-articles-card">
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-xl sm:text-3xl font-bold">{stats.total_articles}</CardTitle>
              <p className="text-xs sm:text-sm text-slate-600">Articles</p>
            </CardHeader>
          </Card>
          <Card data-testid="stat-scores-card">
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-xl sm:text-3xl font-bold">{stats.total_scores}</CardTitle>
              <p className="text-xs sm:text-sm text-slate-600">Scores</p>
            </CardHeader>
            </Card>
          <Card data-testid="stat-topics-card">
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-xl sm:text-3xl font-bold">{stats.total_topics}</CardTitle>
              <p className="text-xs sm:text-sm text-slate-600">Topics</p>
            </CardHeader>
          </Card>
        </div>

        {/* SEO Dashboard */}
        {seoDashboard && (
          <Card className="mb-6 border-teal-200" data-testid="seo-dashboard-card">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-teal-800">
                  <Globe className="h-4 w-4" /> Academic SEO & Indexing
                </CardTitle>
                <div className="flex gap-2">
                  {seoDashboard.without_seo > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50 h-7 text-xs"
                      onClick={handleBulkGenerateSeo}
                      disabled={bulkSeoRunning}
                      data-testid="bulk-generate-seo-btn"
                    >
                      {bulkSeoRunning ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1 h-3 w-3" />}
                      {bulkSeoRunning ? 'Generating...' : `Generate SEO (${seoDashboard.without_seo})`}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-teal-300 text-teal-700 hover:bg-teal-50 h-7 text-xs"
                    onClick={handleSubmitSitemap}
                    disabled={submittingSitemap}
                    data-testid="notify-engines-btn"
                  >
                    {submittingSitemap ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Globe className="mr-1 h-3 w-3" />}
                    {submittingSitemap ? 'Submitting...' : 'Notify Search Engines'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {/* Google Verification */}
              <div className="flex items-center gap-2 mb-3 text-xs">
                <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                  Google Search Console Verified
                </span>
                <span className="text-slate-400">Canonical: /api/seo/render/:id</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-teal-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-teal-700">{seoDashboard.seo_coverage_pct}%</p>
                  <p className="text-xs text-teal-600">SEO Coverage</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{seoDashboard.with_complete_seo}</p>
                  <p className="text-xs text-green-600">SEO Complete</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${seoDashboard.without_seo > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                  <p className={`text-2xl font-bold ${seoDashboard.without_seo > 0 ? 'text-amber-700' : 'text-green-700'}`}>{seoDashboard.without_seo}</p>
                  <p className={`text-xs ${seoDashboard.without_seo > 0 ? 'text-amber-600' : 'text-green-600'}`}>Missing SEO</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{seoDashboard.pdf_coverage_pct}%</p>
                  <p className="text-xs text-blue-600">PDF Available</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-100 rounded-full h-2 mb-3">
                <div className="bg-teal-500 h-2 rounded-full transition-all" style={{ width: `${seoDashboard.seo_coverage_pct}%` }} />
              </div>

              {/* Missing SEO articles */}
              {seoDashboard.articles_missing_seo?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-slate-500 mb-2">Articles missing SEO ({seoDashboard.without_seo}):</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {seoDashboard.articles_missing_seo.map(a => (
                      <div key={a.id} className="flex items-center justify-between text-xs bg-amber-50 rounded px-2 py-1.5">
                        <span className="text-slate-700 truncate mr-2">{a.title}</span>
                        <span className="text-amber-600 whitespace-nowrap">{a.missing.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submission History */}
              {seoSubmissions.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 mb-2">Recent Indexing Activity</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {seoSubmissions.slice(0, 5).map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-slate-500">
                        <span>{s.type === 'bulk' ? 'Bulk submission' : s.type === 'bulk_seo_generated' ? `SEO generated: ${s.seo_title || ''}` : 'Notification'}</span>
                        <span className="text-slate-400">{new Date(s.submitted_at).toLocaleDateString('bg-BG', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Article Quality Checklist */}
              {seoDashboard.quality_checklist?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-slate-500">Article Quality Checklist (avg: {seoDashboard.avg_quality}%)</p>
                    <div className="flex gap-3 text-xs text-slate-400">
                      <span>FAQ: {seoDashboard.with_faq || 0}</span>
                      <span>Abstract: {seoDashboard.with_abstract || 0}/{seoDashboard.total_articles}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {seoDashboard.quality_checklist.map(a => (
                      <div key={a.id} className="flex items-center gap-2 text-xs">
                        <div className="w-16 bg-slate-100 rounded-full h-1.5 shrink-0">
                          <div className={`h-1.5 rounded-full transition-all ${a.score >= 80 ? 'bg-green-500' : a.score >= 50 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${a.score}%` }} />
                        </div>
                        <span className="w-8 text-right font-medium text-slate-600">{a.score}%</span>
                        <span className="text-slate-700 truncate flex-1">{a.title}</span>
                        {a.missing.length > 0 && <span className="text-red-400 whitespace-nowrap text-[10px]">{a.missing.slice(0, 3).join(', ')}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-6">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="ingest-article-btn">
                <Plus className="mr-2 h-4 w-4" />
                Ingest Article
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="ingest-dialog">
              <DialogHeader>
                <DialogTitle>Ingest New Article</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleIngest} className="space-y-4">
                <div>
                  <Label htmlFor="doi">DOI (optional)</Label>
                  <Input
                    id="doi"
                    data-testid="ingest-doi-input"
                    placeholder="10.1056/NEJMoa2034577"
                    value={ingestForm.doi}
                    onChange={(e) => setIngestForm({ ...ingestForm, doi: e.target.value })}
                  />
                  <p className="text-xs text-slate-500 mt-1">If provided, metadata will be fetched automatically from Crossref</p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Manual entry (used when no DOI)</p>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        data-testid="ingest-title-input"
                        placeholder="Article title"
                        value={ingestForm.title}
                        onChange={(e) => setIngestForm({ ...ingestForm, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="authors">Authors (comma-separated)</Label>
                      <Input
                        id="authors"
                        data-testid="ingest-authors-input"
                        placeholder="John Smith, Jane Doe"
                        value={ingestForm.authors}
                        onChange={(e) => setIngestForm({ ...ingestForm, authors: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="journal">Journal</Label>
                        <Input
                          id="journal"
                          data-testid="ingest-journal-input"
                          placeholder="Journal name"
                          value={ingestForm.journal}
                          onChange={(e) => setIngestForm({ ...ingestForm, journal: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="year">Publication Year</Label>
                        <Input
                          id="year"
                          data-testid="ingest-year-input"
                          type="number"
                          placeholder="2024"
                          value={ingestForm.publication_year}
                          onChange={(e) => setIngestForm({ ...ingestForm, publication_year: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="abstract">Abstract</Label>
                      <Textarea
                        id="abstract"
                        data-testid="ingest-abstract-input"
                        placeholder="Article abstract..."
                        value={ingestForm.abstract}
                        onChange={(e) => setIngestForm({ ...ingestForm, abstract: e.target.value })}
                        rows={4}
                      />
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={loading} data-testid="ingest-submit-btn">
                  {loading ? 'Processing...' : 'Ingest Article'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="config-weights-btn">
                <Settings className="mr-2 h-4 w-4" />
                Configure Weights
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="weights-dialog">
              <DialogHeader>
                <DialogTitle>Visibility Score Weights</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Views Weight: {weights.views_weight}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={weights.views_weight}
                    onChange={(e) => setWeights({ ...weights, views_weight: parseFloat(e.target.value) })}
                    data-testid="weight-views-input"
                  />
                </div>
                <div>
                  <Label>Downloads Weight: {weights.downloads_weight}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={weights.downloads_weight}
                    onChange={(e) => setWeights({ ...weights, downloads_weight: parseFloat(e.target.value) })}
                    data-testid="weight-downloads-input"
                  />
                </div>
                <div>
                  <Label>Twitter Weight: {weights.twitter_weight}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={weights.twitter_weight}
                    onChange={(e) => setWeights({ ...weights, twitter_weight: parseFloat(e.target.value) })}
                    data-testid="weight-twitter-input"
                  />
                </div>
                <div>
                  <Label>Citation Weight: {weights.citation_weight}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={weights.citation_weight}
                    onChange={(e) => setWeights({ ...weights, citation_weight: parseFloat(e.target.value) })}
                    data-testid="weight-citation-input"
                  />
                </div>
                <Button onClick={handleUpdateWeights} data-testid="update-weights-btn">
                  Update Weights
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button 
            variant="outline" 
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="recalculate-visibility-btn"
            onClick={async () => {
              try {
                toast.info('Visibility recalculation started...');
                const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                const res = await fetch(`${API_URL}/api/admin/trigger-visibility-recalculation`, { method: 'POST' });
                if (res.status === 409) {
                  toast.warning('Recalculation already in progress');
                  return;
                }
                const data = await res.json();
                toast.success(data.message || 'Recalculation triggered');
                // Poll for completion
                const poll = setInterval(async () => {
                  const statusRes = await fetch(`${API_URL}/api/admin/periodic-recalculation-status`);
                  const status = await statusRes.json();
                  if (!status.running && status.last_result) {
                    clearInterval(poll);
                    toast.success(`Visibility scores updated: ${status.last_result.updated}/${status.last_result.total} articles (${status.last_result.duration_seconds}s)`);
                    loadArticles();
                  }
                }, 3000);
                setTimeout(() => clearInterval(poll), 120000);
              } catch (e) {
                toast.error('Failed: ' + e.message);
              }
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Recalculate All Visibility
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Study Type</Label>
                <Select value={filters.study_type} onValueChange={(value) => setFilters({ ...filters, study_type: value })}>
                  <SelectTrigger data-testid="filter-study-type">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="RCT">RCT</SelectItem>
                    <SelectItem value="Meta-analysis">Meta-analysis</SelectItem>
                    <SelectItem value="Prospective cohort">Prospective cohort</SelectItem>
                    <SelectItem value="Retrospective cohort">Retrospective cohort</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Evidence Level</Label>
                <Select value={filters.evidence_level} onValueChange={(value) => setFilters({ ...filters, evidence_level: value })}>
                  <SelectTrigger data-testid="filter-evidence-level">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Level I">Level I</SelectItem>
                    <SelectItem value="Level II">Level II</SelectItem>
                    <SelectItem value="Level III">Level III</SelectItem>
                    <SelectItem value="Level IV">Level IV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={loadArticles} data-testid="apply-filters-btn">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles Table */}
        <Card data-testid="articles-table-card">
          <CardHeader>
            <CardTitle>Articles ({filteredArticles.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-0 sm:px-6">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Journal</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Study Type</TableHead>
                  <TableHead>Evidence</TableHead>
                  <TableHead>Impact Index</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredArticles.map((article) => (
                  <TableRow key={article.id} data-testid={`article-row-${article.id}`}>
                    <TableCell className="font-medium max-w-xs truncate">
                      <Link to={`/article/${article.id}`} className="text-blue-600 hover:underline">
                        {article.title}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">{article.journal || 'N/A'}</TableCell>
                    <TableCell>{article.publication_year || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" data-testid="study-type-badge">{article.normalized_study_type || 'Unknown'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getEvidenceLevelColor(article.evidence_level)} data-testid="evidence-level-badge">
                        {article.evidence_level || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-lg" data-testid="impact-index-value">
                        {article.impact_index ? article.impact_index.toFixed(1) : 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" data-testid="article-status-badge">{article.article_status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleAnalyzeArticle(article.id, article.title)}
                          disabled={analyzingId === article.id}
                          data-testid={`analyze-article-btn-${article.id}`}
                          title="AI Research Analysis"
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        >
                          <Brain className={`h-4 w-4 ${analyzingId === article.id ? 'animate-pulse' : ''}`} />
                        </Button>
                        {article.has_pdf && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleReanalyzeArticle(article.id)}
                            disabled={analyzingId === article.id}
                            data-testid={`reanalyze-btn-${article.id}`}
                            title="Re-analyze with full PDF text"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <RefreshCw className={`h-4 w-4 ${analyzingId === article.id ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openPdfDialog(article)}
                          title="Upload PDF"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          data-testid={`upload-pdf-btn-${article.id}`}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditArticle(article)}
                          data-testid={`edit-article-btn-${article.id}`}
                          title="Edit article"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        {article.doi && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRecalculate(article.doi)}
                            data-testid="recalculate-btn"
                            title="Recalculate score"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-article-btn-${article.id}`}
                              title="Delete article"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid="delete-confirm-dialog">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the article "{article.title.substring(0, 60)}..."
                                and all associated data including scores and topic links. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteArticle(article.id, article.title)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="delete-confirm-btn"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      
      {/* Edit Article Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Article</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-abstract">Abstract</Label>
              <Textarea
                id="edit-abstract"
                data-testid="edit-abstract"
                value={editForm.abstract}
                onChange={(e) => setEditForm({ ...editForm, abstract: e.target.value })}
                rows={5}
                placeholder="Article abstract..."
              />
            </div>

            <div>
              <Label htmlFor="edit-faq">FAQ (Q&A pairs, one per line: Q: question / A: answer)</Label>
              <Textarea
                id="edit-faq"
                data-testid="edit-faq"
                value={editForm.faq}
                onChange={(e) => setEditForm({ ...editForm, faq: e.target.value })}
                rows={4}
                placeholder={"Q: What is the main finding?\nA: The study found that...\n\nQ: What was the sample size?\nA: 110 patients were enrolled."}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-authors">Authors (comma-separated)</Label>
                <Input
                  id="edit-authors"
                  value={editForm.authors}
                  onChange={(e) => setEditForm({ ...editForm, authors: e.target.value })}
                  placeholder="Smith J, Doe A, etc."
                />
              </div>
              
              <div>
                <Label htmlFor="edit-journal">Journal</Label>
                <Input
                  id="edit-journal"
                  value={editForm.journal}
                  onChange={(e) => setEditForm({ ...editForm, journal: e.target.value })}
                  placeholder="Nature Medicine"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-year">Publication Year</Label>
                <Input
                  id="edit-year"
                  type="number"
                  value={editForm.publication_year}
                  onChange={(e) => setEditForm({ ...editForm, publication_year: e.target.value })}
                  placeholder="2023"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-doi">DOI</Label>
                <Input
                  id="edit-doi"
                  value={editForm.doi}
                  onChange={(e) => setEditForm({ ...editForm, doi: e.target.value })}
                  placeholder="10.1056/..."
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-study-type">Study Type</Label>
                <Input
                  id="edit-study-type"
                  value={editForm.raw_study_type}
                  onChange={(e) => setEditForm({ ...editForm, raw_study_type: e.target.value })}
                  placeholder="RCT, Cohort Study, etc."
                />
              </div>
              
              <div>
                <Label htmlFor="edit-sample-size">Sample Size</Label>
                <Input
                  id="edit-sample-size"
                  type="number"
                  value={editForm.sample_size}
                  onChange={(e) => setEditForm({ ...editForm, sample_size: e.target.value })}
                  placeholder="1000"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-followup">Follow-up Duration (months)</Label>
              <Input
                id="edit-followup"
                type="number"
                value={editForm.follow_up_duration_months}
                onChange={(e) => setEditForm({ ...editForm, follow_up_duration_months: e.target.value })}
                placeholder="12"
              />
            </div>

            {/* SEO Section */}
            <div className="border-t-2 border-teal-200 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-teal-800">SEO & Discovery</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  onClick={handleGenerateAcademicSeo}
                  disabled={generatingSeo}
                  data-testid="generate-academic-seo-btn"
                >
                  {generatingSeo ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                      Generate Academic SEO
                    </>
                  )}
                </Button>
              </div>
              {generatingSeo && (
                <div className="bg-teal-50 border border-teal-200 rounded p-3 mb-3 text-sm text-teal-800">
                  Analyzing article content and generating MeSH-optimized SEO for PubMed, Google Scholar, Scopus...
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit-seo-title">SEO Title (55-65 chars)</Label>
                  <Input
                    id="edit-seo-title"
                    data-testid="edit-seo-title"
                    value={editForm.seo_title}
                    onChange={(e) => setEditForm({ ...editForm, seo_title: e.target.value })}
                    placeholder="Optimized title for search engines"
                    maxLength={70}
                  />
                  <p className="text-xs text-slate-400 mt-0.5">{(editForm.seo_title || '').length}/65 chars</p>
                </div>
                <div>
                  <Label htmlFor="edit-meta-desc">Meta Description (max 155 chars)</Label>
                  <Textarea
                    id="edit-meta-desc"
                    data-testid="edit-meta-description"
                    value={editForm.meta_description}
                    onChange={(e) => setEditForm({ ...editForm, meta_description: e.target.value })}
                    placeholder="Brief description for search results..."
                    rows={2}
                    maxLength={160}
                  />
                  <p className="text-xs text-slate-400 mt-0.5">{(editForm.meta_description || '').length}/155 chars</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-seo-keywords">Keywords (comma-separated)</Label>
                    <Input
                      id="edit-seo-keywords"
                      data-testid="edit-seo-keywords"
                      value={editForm.seo_keywords}
                      onChange={(e) => setEditForm({ ...editForm, seo_keywords: e.target.value })}
                      placeholder="breast cancer, RCT, surgery"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-slug">URL Slug</Label>
                    <Input
                      id="edit-slug"
                      data-testid="edit-slug"
                      value={editForm.slug}
                      onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
                      placeholder="article-url-slug"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="border-t border-slate-200 pt-4 mt-2">
              <p className="text-sm font-bold text-slate-700 mb-3">Social Media</p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit-twitter">Twitter Post</Label>
                  <Input
                    id="edit-twitter"
                    data-testid="edit-twitter"
                    value={editForm.twitter_text}
                    onChange={(e) => setEditForm({ ...editForm, twitter_text: e.target.value })}
                    placeholder="Article highlight for Twitter..."
                    maxLength={280}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-linkedin">LinkedIn Summary</Label>
                  <Textarea
                    id="edit-linkedin"
                    data-testid="edit-linkedin"
                    value={editForm.linkedin_summary}
                    onChange={(e) => setEditForm({ ...editForm, linkedin_summary: e.target.value })}
                    placeholder="Professional summary for LinkedIn..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* PDF Upload Dialog */}
      <Dialog open={isPdfDialogOpen} onOpenChange={setIsPdfDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Upload PDF for Article
            </DialogTitle>
          </DialogHeader>
          {selectedArticleForPdf && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded">
                <p className="text-sm font-semibold text-slate-900">{selectedArticleForPdf.title}</p>
                <p className="text-xs text-slate-600 mt-1">
                  {selectedArticleForPdf.journal} ({selectedArticleForPdf.publication_year})
                </p>
              </div>
              
              <div>
                <Label htmlFor="pdf-file">Select PDF File</Label>
                <Input
                  id="pdf-file"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  className="mt-2"
                />
                {pdfFile && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Selected: {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm text-blue-900">
                <p className="font-semibold mb-1">What will happen:</p>
                <ul className="space-y-1 ml-4 list-disc">
                  <li>PDF will be saved permanently</li>
                  <li>Full text will be extracted</li>
                  <li>AI analysis will be re-run with full text</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handlePdfUpload(selectedArticleForPdf.id)}
                  disabled={!pdfFile || uploadingPdfFor === selectedArticleForPdf.id}
                  className="flex-1"
                >
                  {uploadingPdfFor === selectedArticleForPdf.id ? (
                    <>
                      <Upload className="mr-2 h-4 w-4 animate-pulse" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload & Analyze
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsPdfDialogOpen(false);
                    setPdfFile(null);
                  }}
                  disabled={uploadingPdfFor === selectedArticleForPdf.id}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


    </div>
  );
};

export default AdminDashboard;
